//#region components
import useAPIClientHelper from "./useAPIClientHelper";
//#endregion
//#region types
import { AuthType } from "models/enums/auth";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceConstants } from "utils/constants";
import { CurrencyCode } from "models/enums/currency";
import { InvoiceFormTypes } from "models/formTypes";
//#endregion
import { DateHelper } from "utils/helpers";

function useSelfInvoiceService() {
  const { apiClientInstance } = useAPIClientHelper();

  const markAsSubmitted = (id: string) => {
    return apiClientInstance(AuthType.Company).SelfInvoiceClient.MarkSelfInvoiceAsCreatedAsync(id);
  };

  const reject = (id: string) => {
    return apiClientInstance(AuthType.Company).SelfInvoiceClient.MarkSelfInvoiceAsRejectedAsync(id);
  };

  const get = (id: string) => {
    return apiClientInstance(AuthType.Company)
      .SelfInvoiceClient.GetAsync(id)
      .then((res) => res.data);
  };

  const convertFormToAPIRequest = (
    form: InvoiceFormTypes.SelfInvoice,
    invoice: WintSuperkollAPIModel.SelfInvoice,
    calculatePrice?: boolean
  ) => {
    const post: WintSuperkollAPIModel.InvoicePost = {
      ownerId: form.ownerId,
      attachForwardedCostDocuments: false,
      customerId: (form.customer?.id as number) ?? 0,
      rows: form.products?.map((product, index) => {
        const periodisation = form.isPeriodisationAdvancedConfiguration
          ? product.periodisation
          : form.products![0].periodisation;

        const amount = product.totalAmountExTax ?? 0;
        const quantity = product.quantity ?? 0;

        return {
          id: product.id ?? 0,
          articleId: Number(product.article?.id ?? 0),
          orderIndex: index,
          quantity: Number(quantity),
          unit: product.unit ? { id: product.unit } : { id: 0 },
          unitPrice: calculatePrice ? (quantity > 0 ? amount / quantity : 0) : Number(product.price ?? 0),
          vat: Number(product.tax),
          overriddenVat: Number(product.tax),
          description: product.article?.value ?? "",
          overriddenDescription: product.text ?? "",
          overriddenUnitPrice: Number(product.price ?? 0),
          euVatProductType: product.eUVatCategoryId,
          periodisation: periodisation?.range?.start !== undefined && periodisation?.range?.end !== undefined,
          periodisationStartMonth: periodisation?.range?.start
            ? DateHelper.getMonth(periodisation.range.start)
            : undefined,
          periodisationStartYear: periodisation?.range?.start
            ? DateHelper.getYear(periodisation.range.start)
            : undefined,
          periodisationEndMonth: periodisation?.range?.end ? DateHelper.getMonth(periodisation.range.end) : undefined,
          periodisationEndYear: periodisation?.range?.end ? DateHelper.getYear(periodisation.range.end) : undefined,
          dimensions: form.isDimensionsAdvancedConfiguration ? product.dimensions : [],
          rut: false,
          rot: false,
        };
      }),
      deliveryDate: invoice.invoiceDate ?? new Date(),
      postingDate: form.invoiceDate ?? invoice.invoiceDate ?? new Date(),
      dueDate: invoice.dueDate ?? new Date(),
      showReferencePersonInAddress: true,
      showShippingAddress: true,
      showCurrencyCode: true,
      language: InvoiceConstants.DefaultInvoiceLanguageCode,
      currency: form.currency ?? CurrencyCode.SEK,
      attachments: invoice.images?.map((image, index) => ({
        contentType: image.contentType,
        data: image.content,
        fileName: `SelfInvoice_${invoice.id}_${index}`,
      })),
      invoiceHouseWorkType: WintSuperkollAPIModel.InvoiceHouseWorkType.None,
      invoiceRotPropertyType: WintSuperkollAPIModel.InvoiceRotPropertyType.None,
      invoiceSource: WintSuperkollAPIModel.InvoiceSource.SelfInvoice,
      useStrictValidation: true,
      originOcrNumber: form.oCRNumber,
      selfInvoiceId: invoice.id,
      dimensions: form.isDimensionsAdvancedConfiguration
        ? []
        : form.products
          ? (form.products[0]?.dimensions ?? [])
          : [],
      invoiceRotPercentageType: WintSuperkollAPIModel.InvoiceRotPercentageType.Standard,
    };

    return post;
  };

  const getInvoiceItemTotal = (products: InvoiceFormTypes.Product[]) => {
    return products.map((row) => Number(row.totalAmountExTax ?? 0)).reduce((x, y) => x + y, 0) ?? 0;
  };

  const getInvoiceItemTaxTotal = (products: InvoiceFormTypes.Product[]) => {
    return (
      products.map((row) => (Number(row.totalAmountExTax ?? 0) * Number(row.tax)) / 100).reduce((x, y) => x + y, 0) ?? 0
    );
  };

  const getRowDimensionData = (value?: InvoiceFormTypes.Product[]) => {
    const selectedDimensions =
      value?.flatMap(
        (product: InvoiceFormTypes.Product) =>
          product.dimensions?.map(
            (dimension): WintSuperkollAPIModel.DimensionData => ({
              dimensionId: dimension,
              amount: product.totalAmountExTax ?? 0,
            })
          ) ?? []
      ) ?? [];

    const result: WintSuperkollAPIModel.DimensionData[] = [];

    selectedDimensions.reduce<Record<string, WintSuperkollAPIModel.DimensionData>>(
      (res: Record<string, WintSuperkollAPIModel.DimensionData>, value: WintSuperkollAPIModel.DimensionData) => {
        if (value.dimensionId) {
          if (!res[value.dimensionId]) {
            res[value.dimensionId] = {
              dimensionId: value.dimensionId,
              amount: value.amount,
            };
            result.push(res[value.dimensionId]);
          } else if (res[value.dimensionId]) {
            res[value.dimensionId].amount += value.amount;
          }
        }
        return res;
      },
      {}
    );

    return result;
  };

  const getInvoiceTemplate = (id: string) => {
    return apiClientInstance(AuthType.Company)
      .SelfInvoiceClient.GetTemplateInvoiceFromSelfInvoiceAsync(id)
      .then((res) => res.data);
  };

  const getCustomerInvoiceId = (id: string) => {
    return apiClientInstance(AuthType.Company)
      .SelfInvoiceClient.GetCustomerInvoiceId(id)
      .then((res) => res.data);
  };

  return {
    markAsSubmitted,
    reject,
    getInvoiceTemplate,
    get,
    convertFormToAPIRequest,
    getInvoiceItemTotal,
    getInvoiceItemTaxTotal,
    getRowDimensionData,
    getCustomerInvoiceId,
  };
}

export default useSelfInvoiceService;
